import { AdAccountDTO } from 'api-models'
import { IntegrationProvider } from '@clickguard/design-system'
import { currencyList } from 'utils/currency'

/** Currency options for the ad-account settings selects. */
export const currenciesOptions: string[] = currencyList

/** Convert a `{period, unit}` pair into seconds (audience-exclusion period). */
export const transformPeriodToSeconds = (period: string, type: string) => {
	if (type === 'hours') {
		return Number(period) * 60 * 60
	}
	if (type === 'minutes') {
		return Number(period) * 60
	}
	if (type === 'days') {
		return Number(period) * 60 * 60 * 24
	}
	return Number(period)
}

/** Convert seconds back into the largest whole `{period, type}` unit. */
export const transformSecondsToPeriod = (seconds: number) => {
	if (seconds < 60) {
		return {
			period: seconds.toString(),
			type: 'seconds'
		}
	}
	if (seconds < 3600) {
		return {
			period: Math.floor(seconds / 60).toString(),
			type: 'minutes'
		}
	}
	if (seconds < 86400) {
		return {
			period: Math.floor(seconds / 3600).toString(),
			type: 'hours'
		}
	}
	return {
		period: Math.floor(seconds / 86400).toString(),
		type: 'days'
	}
}

/** Loose view over the platform-specific ad-account shapes we read here. */
type AnyAdAccount = AdAccountDTO & {
	details?: { status?: string; account_number?: string }
	preferences?: { is_tracking_healthy?: boolean } & Record<string, unknown>
}

/** Map our platform code to the DS IntegrationLogo provider id. */
export const platformToProvider = (platform: string): IntegrationProvider =>
	platform === 'GADS'
		? 'google-ads'
		: platform === 'BADS'
		? 'microsoft'
		: 'meta'

/** External ad-account id, with Meta/Bing's `act_` prefix stripped. */
export const formatAccountId = (adAccount: AdAccountDTO): string =>
	((adAccount as AnyAdAccount).details?.account_number ?? '').replace(
		'act_',
		''
	)

export type ConnectionStatusView = {
	text: string
	color: 'success' | 'error' | 'warning' | 'gray'
}

/**
 * The row's connection badge, or `null` when the credential is healthy.
 *
 * `details.status` carries more than OK and NO_ACCESS: PENDING, INITIALIZING,
 * SYNCHRONIZING and — on Google — INVITED are transient states every just-linked or
 * still-syncing account passes through (`google-ads-ad-account.ts:94-98`). Rendering
 * them as "No access" sends the customer to re-authorize a credential that is working,
 * and for INVITED the real next step is the link request already waiting for them.
 *
 * An ABSENT status is amber too, not red: an account still being created has no
 * credential problem to report.
 */
export const connectionStatusView = (
	adAccount: AdAccountDTO
): ConnectionStatusView | null => {
	const status = (adAccount as AnyAdAccount).details?.status
	if (status === 'OK') return null
	if (status === 'NO_ACCESS') return { text: 'No access', color: 'error' }
	const text = status
		? status.charAt(0).toUpperCase() +
		  status.slice(1).toLowerCase().replaceAll('_', ' ')
		: 'Inactive'
	return { text, color: 'warning' }
}

/**
 * An ACTIONABLE connection problem, which is what "Pending issues" is a list of — so
 * only NO_ACCESS. The transient states above resolve on their own and need nothing from
 * the user; counting them made the quick filter a to-do list of things not to do.
 */
export const hasConnectionIssue = (adAccount: AdAccountDTO): boolean =>
	(adAccount as AnyAdAccount).details?.status === 'NO_ACCESS'

/** Tracking issue — only meaningful for Google/Microsoft; false/absent = healthy. */
export const hasTrackingIssue = (adAccount: AdAccountDTO): boolean =>
	(adAccount as AnyAdAccount).preferences?.is_tracking_healthy === false

/**
 * The single "issue" definition shared by the row warning icon and the
 * Pending-issues quick filter (DEV-2352 §2.1): connection status ≠ Active OR a
 * tracking-configuration issue.
 */
export const hasIssue = (adAccount: AdAccountDTO): boolean =>
	hasConnectionIssue(adAccount) || hasTrackingIssue(adAccount)

/** Filter value used by the "Click detection" filter group. */
export const protectionFilterValue = (adAccount: AdAccountDTO): string =>
	adAccount.status === 'ACTIVE' ? 'ON' : 'OFF'

/**
 * Human ordering for names and ids: `numeric` makes `493-201-8846` sort the way a
 * reader scans it rather than character by character, so `10` follows `9`.
 */
export const idCollator = new Intl.Collator(undefined, {
	numeric: true,
	sensitivity: 'base'
})

/** Campaigns this ad account is set to ignore — the stored click-detection "off" set. */
export const ignoredCampaignIds = (adAccount: AdAccountDTO): string[] =>
	((adAccount as AnyAdAccount).preferences?.ignored_campaigns as string[]) ?? []

/**
 * What to call an ad account on screen.
 *
 * `name` is genuinely empty on some accounts (it is a user-editable field that is never
 * required), which left the table showing a bare platform logo and the campaign drawer
 * reading "follows the settings of ." — so fall back to the account number, then to a
 * placeholder, and never render an empty string.
 */
export const adAccountLabel = (adAccount: AdAccountDTO): string =>
	adAccount.name?.trim() || formatAccountId(adAccount) || 'Untitled account'
